import React, { useState } from 'react';
import './index.css';
import { useAuth } from './hooks/useAuth';
import { useQuotes } from './hooks/useQuotes';
import { STORAGE_KEYS } from './utils/helpers';
import { Quote } from './types';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import NewQuote from './pages/NewQuote/index';
import QuoteHistory from './pages/QuoteHistory';
import WalkInAlbum from './pages/WalkInAlbum';
import WalkInPP from './pages/WalkInPP';
import BookingCalendar from './pages/BookingCalendar';
import Settings from './pages/Settings';
import GeneralPlanner from './pages/GeneralPlanner';
import EnquiryTracker from './pages/EnquiryTracker';
import BookingTracker from './pages/BookingTracker';
import MasterEnquiry from './pages/MasterEnquiry';
import LifecycleMap from './pages/LifecycleMap';
import LessonLearned from './pages/LessonLearned';
import CostManagement from './pages/CostManagement';
import PostProductionTracker from './pages/PostProductionTracker';
import AuditChecklist from './pages/AuditChecklist';

type Page = 'dashboard' | 'new-quote' | 'quote-history' | 'walk-in-album' | 'walk-in-pp' | 'calendar' | 'settings' | 'general-planner' | 'enquiry-tracker' | 'booking-tracker' | 'master-enquiry' | 'cost-management' | 'pp-tracker' | 'audit-checklist' | 'lifecycle-map' | 'lesson-learned';

export default function App() {
  const { currentUser, logout, refreshAuth } = useAuth();
  const {
    saveQuote, saveWalkInAlbumQuote, saveWalkInPPQuote,
    saveGeneralPlannerQuote, allQuotesForDisplay, deleteAnyQuote, updateAnyQuoteStatus, convertQuoteToInvoice, markAdvanceReceived,
  } = useQuotes();
  const [page, setPage] = useState<Page>('dashboard');
  const [quoteFlowMode, setQuoteFlowMode] = useState<'quick' | 'link-enquiry' | 'new-lifecycle' | null>(null);
  const [showModeSelect, setShowModeSelect] = useState(true); // show on first load after login
  const [showGuide, setShowGuide] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!currentUser);
  // Quote currently being edited (null = New Quote starts blank)
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  // Set when arriving from Enquiry Tracker's "Build Quote" button — New
  // Quote skips straight to the wizard for this exact enquiry, no search.
  const [prelinkedEnquiry, setPrelinkedEnquiry] = useState<any | null>(null);

  const handleLogin = () => { refreshAuth(); setIsLoggedIn(true); };
  const handleLogout = () => { logout(); setIsLoggedIn(false); };

  const handleClearData = () => {
    Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
    window.location.reload();
  };

  const handleNavigate = (p: Page) => {
    setEditingQuote(null); // any generic nav click (including Sidebar's "New Quote") starts blank
    setPrelinkedEnquiry(null);
    if (p === 'new-quote') {
      // Always ask fresh which of the 3 flows to use — previously this
      // silently reused whatever mode was picked last time, which looked
      // like "New Quote" was stuck doing the same thing every time.
      setQuoteFlowMode(null);
      setShowModeSelect(true);
      return;
    }
    setPage(p);
  };

  const handleEditQuote = (quote: Quote) => {
    setEditingQuote(quote);
    setPage('new-quote');
  };

  // From Enquiry Tracker's "Build Quote" button — jumps straight into the
  // quote wizard for this exact enquiry, skipping New Quote's own search
  // screen entirely since the enquiry is already picked.
  const handleBuildQuoteFromEnquiry = (enquiry: any) => {
    setEditingQuote(null);
    setPrelinkedEnquiry(enquiry);
    setQuoteFlowMode('link-enquiry');
    setShowModeSelect(false);
    setPage('new-quote');
  };

  if (!isLoggedIn || !currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  const renderPage = () => {
    switch (page) {
      case 'dashboard':
        return <Dashboard user={currentUser} quotes={allQuotesForDisplay} onNavigate={handleNavigate} onEditQuote={handleEditQuote} onUpdateStatus={updateAnyQuoteStatus} onConvertToInvoice={convertQuoteToInvoice} onMarkAdvanceReceived={markAdvanceReceived} />;
      case 'new-quote':
        return (
          <NewQuote
            user={currentUser}
            initialData={editingQuote || undefined}
            initialMode={editingQuote ? 'quick' : (quoteFlowMode || undefined)}
            prelinkedEnquiry={prelinkedEnquiry || undefined}
            onSave={saveQuote}
            onSaved={() => { setEditingQuote(null); setQuoteFlowMode(null); setPrelinkedEnquiry(null); setPage('quote-history'); }}
          />
        );
      case 'quote-history':
        return <QuoteHistory quotes={allQuotesForDisplay} user={currentUser} onDelete={deleteAnyQuote} onEdit={handleEditQuote} onUpdateStatus={updateAnyQuoteStatus} onConvertToInvoice={convertQuoteToInvoice} onMarkAdvanceReceived={markAdvanceReceived} />;
      case 'walk-in-album':return <WalkInAlbum onSave={saveWalkInAlbumQuote} />;
      case 'walk-in-pp':   return <WalkInPP onSave={saveWalkInPPQuote} />;
      case 'calendar':     return <BookingCalendar quotes={allQuotesForDisplay} />;
      case 'general-planner':
        return <GeneralPlanner onSave={(q: any) => saveGeneralPlannerQuote({ ...q, createdBy: currentUser.name })} />;
      case 'enquiry-tracker':
        return <EnquiryTracker user={currentUser} onBuildQuote={handleBuildQuoteFromEnquiry} />;
      case 'booking-tracker':
        return <BookingTracker user={currentUser} />;
      case 'lifecycle-map':
        return <LifecycleMap user={currentUser} />;
      case 'master-enquiry':
        return <MasterEnquiry user={currentUser} />;
      case 'cost-management':
        return <CostManagement user={currentUser} />;
      case 'pp-tracker':
        return <PostProductionTracker user={currentUser} />;
      case 'audit-checklist':
        return <AuditChecklist user={currentUser} />;
      case 'lesson-learned':
        return <LessonLearned user={currentUser} />;
      case 'settings':
        if (currentUser.role !== 'admin') return <div style={{ padding: 28, color: 'var(--text-muted)' }}>Access denied.</div>;
        return <Settings user={currentUser} onClearData={handleClearData} />;
      default:
        return <Dashboard user={currentUser} quotes={allQuotesForDisplay} onNavigate={handleNavigate} onEditQuote={handleEditQuote} onUpdateStatus={updateAnyQuoteStatus} onConvertToInvoice={convertQuoteToInvoice} onMarkAdvanceReceived={markAdvanceReceived} />;
    }
  };

  // Full-screen mode selection — covers entire screen, no sidebar
  if (showModeSelect) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: '#F9FAFB',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 40
      }}>
        {/* Logo */}
        <div style={{ marginBottom: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937', letterSpacing: '-0.5px' }}>Shivam Studio</div>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Welcome back, {currentUser?.name}. What would you like to do?</div>
        </div>

        {/* 3 cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 340px)', gap: 24, maxWidth: 1100 }}>

          {/* Quick Quote */}
          <div onClick={() => { setShowModeSelect(false); setQuoteFlowMode('quick'); setPage('new-quote'); }}
            style={{ padding: 36, borderRadius: 16, border: '2px solid #E5E7EB', cursor: 'pointer',
              background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.15s' }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#B8860B'; el.style.boxShadow = '0 8px 32px rgba(184,134,11,0.12)'; el.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#E5E7EB'; el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; el.style.transform = 'none'; }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚡</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: '#1F2937' }}>Quick Quote</div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.7, marginBottom: 20 }}>
              For walk-in customers, rate checking, or a quick reference estimate. Just the planner — no lifecycle tracking needed.
            </div>
            <div style={{ fontSize: 12, color: '#B8860B', fontWeight: 600, padding: '6px 12px', background: '#FFFBEB', borderRadius: 6, display: 'inline-block' }}>
              9 steps → Save → Done
            </div>
          </div>

          {/* Planner + Existing Enquiry */}
          <div onClick={() => { setShowModeSelect(false); setPage('enquiry-tracker'); }}
            style={{ padding: 36, borderRadius: 16, border: '2px solid #E5E7EB', cursor: 'pointer',
              background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.15s' }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#0D9488'; el.style.boxShadow = '0 8px 32px rgba(13,148,136,0.12)'; el.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#E5E7EB'; el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; el.style.transform = 'none'; }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: '#1F2937' }}>Planner + Existing Enquiry</div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.7, marginBottom: 20 }}>
              Customer is already in the Enquiry Tracker. Takes you there — find them, then use "Build Quote" on their row.
            </div>
            <div style={{ fontSize: 12, color: '#0D9488', fontWeight: 600, padding: '6px 12px', background: '#F0FDFA', borderRadius: 6, display: 'inline-block' }}>
              Enquiry Tracker → Build Quote → lifecycle continues
            </div>
          </div>

          {/* Planner + New Customer */}
          <div onClick={() => { setShowModeSelect(false); setQuoteFlowMode('new-lifecycle'); setPage('new-quote'); }}
            style={{ padding: 36, borderRadius: 16, border: '2px solid #E5E7EB', cursor: 'pointer',
              background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'all 0.15s' }}
            onMouseEnter={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#7C3AED'; el.style.boxShadow = '0 8px 32px rgba(124,58,237,0.12)'; el.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = '#E5E7EB'; el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; el.style.transform = 'none'; }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🆕</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: '#1F2937' }}>Planner + New Customer</div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.7, marginBottom: 20 }}>
              Brand new customer — not in the system yet. Creates the enquiry and quote together in one go. Full lifecycle starts automatically.
            </div>
            <div style={{ fontSize: 12, color: '#7C3AED', fontWeight: 600, padding: '6px 12px', background: '#F5F3FF', borderRadius: 6, display: 'inline-block' }}>
              Enquiry + Quote created together → Lifecycle begins
            </div>
          </div>

        </div>

        {/* Cancel */}
        <button onClick={() => { setShowModeSelect(false); setPage('dashboard'); }}
          style={{ marginTop: 40, fontSize: 13, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Skip — go to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar currentPage={page} onNavigate={handleNavigate} user={currentUser} onLogout={handleLogout}
        onShowGuide={() => setShowGuide(true)} onSwitchMode={() => setShowModeSelect(true)} />
      <main style={{ marginLeft: 220, flex: 1, minHeight: '100vh', overflowY: 'auto', position: 'relative' }}>
        {/* Mode switch pill — always visible */}
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 50, display: 'flex', gap: 8 }}>
          <button onClick={() => setShowModeSelect(true)}
            style={{ padding: '8px 16px', borderRadius: 100, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', background: '#1F2937', color: '#fff', border: 'none',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚡ Switch Flow
          </button>
        </div>
        {renderPage()}
      </main>

      {/* Navigation Guide panel */}
      {showGuide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          onClick={() => setShowGuide(false)}>
          <div style={{ width: 420, height: '100vh', background: '#fff', overflowY: 'auto', padding: 28, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 17, color: '#1F2937' }}>Navigation Guide</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>What each section does</div>
              </div>
              <button onClick={() => setShowGuide(false)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>×</button>
            </div>

            {[
              { heading: '📊 PLANNER', color: '#B8860B', items: [
                { name: 'Dashboard', desc: 'Overview of all quotes, this month activity, confirmed bookings and value. Recent quotes at a glance.' },
                { name: 'New Quote', desc: 'Create a new customer quotation. Choose from 3 flows: Quick Quote, Link to Existing Enquiry, or New Customer Full Lifecycle.' },
                { name: 'Quote History', desc: 'View, edit, and manage all saved quotes. Convert quotations to invoices. Download studio or customer copy.' },
                { name: 'Walk-in Album', desc: 'Quick album-only quote for walk-in customers who only need album pricing.' },
                { name: 'Walk-in Post Prod.', desc: 'Quick post-production quote without the full package wizard.' },
                { name: 'Booking Calendar', desc: 'Calendar view of all booked event dates to check availability.' },
                { name: 'General Planner', desc: 'Miscellaneous planning and custom quotes outside standard packages.' },
                { name: 'Settings', desc: 'Manage user accounts, app data, and configuration. Admin only.' },
              ]},
              { heading: '🔄 LIFECYCLE MANAGEMENT', color: '#0D9488', items: [
                { name: 'Master Enquiry View', desc: 'Bird\'s eye view of ALL enquiries across ALL services. Filter by service, status, or search by name. See totals at a glance.' },
                { name: 'Enquiry Tracker', desc: 'Log and manage customer enquiries. 7 service tabs: Wedding, Pre-Wedding, Engagement, Anniversary, Birthday, Office Event, Any Event. Create bookings from confirmed enquiries.' },
                { name: 'Booking & Stage Tracker', desc: 'Track each confirmed booking through 7 stages: Customer Document → Contractor Assignment → Readiness → Event Execution (one-way gate) → Post-Production → Final Delivery → Post-Event. Customer Details tab for POC, package, amounts.' },
                { name: 'Post-Production Tracker', desc: 'Track all PP deliverables per booking — Full Event Video, Highlights, Teaser, Reels, Short Film etc. Set vendor, status (Not Started → Delivered), and due dates.' },
                { name: 'Cost Management', desc: 'Track actual costs per booking — contractor payment, PP vendor, album, transport, misc. Mark paid or unpaid. Running totals shown.' },
                { name: 'Event Audit Checklist', desc: 'Pre-event and post-event checklist per booking. Equipment, planning, on-day, and wrap-up sections. Progress bar shows completion. Add custom items.' },
              ]},
              { heading: '🔁 THE FLOW', color: '#5B21B6', items: [
                { name: 'Step 1', desc: 'Customer contacts → Enquiry Tracker → New Enquiry → WENQ ref generated' },
                { name: 'Step 2', desc: 'New Quote → Link to Existing Enquiry → Build package → Save → Share customer copy' },
                { name: 'Step 3', desc: 'Customer confirms → Quote History → Convert to Invoice → invoice sent to customer (payment not required yet)' },
                { name: 'Step 3b', desc: 'Once advance actually lands → Quote History → Confirm Advance Received → this is what starts lifecycle tracking' },
                { name: 'Step 4', desc: 'Enquiry Tracker → Mark Confirmed → Create Booking → Link quote → WCSR ref generated' },
                { name: 'Step 5', desc: 'Booking & Stage Tracker → Advance through 7 stages → Customer Details auto-filled' },
                { name: 'Step 6', desc: 'Post-event → PP Tracker + Cost Management + Audit Checklist → Close the loop' },
              ]},
            ].map(section => (
              <div key={section.heading} style={{ marginBottom: 28 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: section.color, marginBottom: 12, letterSpacing: '0.5px' }}>
                  {section.heading}
                </div>
                {section.items.map(item => (
                  <div key={item.name} style={{ marginBottom: 12, paddingLeft: 12, borderLeft: `3px solid ${section.color}20` }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1F2937', marginBottom: 2 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>{item.desc}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
